import React, { useRef, useState } from "react";
import {
  uploadRiskEvidenceFile,
  uploadControlEvidenceFile,
  uploadRiskEvidenceUrl,
  uploadControlEvidenceUrl,
} from "../api/evidence.api.js";
import { hasModuleAccess } from "../accessControl";
import { useAuth } from "../useAuth";
import Button from "../ui/Button";

const MAX_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg"];

const mapError = (status) => {
  switch (status) {
    case 413:
      return "File too large.";
    case 415:
      return "File type not allowed.";
    case 403:
      return "You don't have permission.";
    case 409:
      return "Evidence still processing.";
    case 503:
      return "Scanning temporarily unavailable.";
    default:
      return "Upload failed.";
  }
};

export function EvidenceUploader({ parentType, parentId, onCreated }) {
  const { user } = useAuth() || {};
  const [mode, setMode] = useState("file");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle | uploading | success | error
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const statusTimer = useRef(null);

  const isRisk = parentType === "risk";
  const moduleKey = isRisk ? "risks" : "controls";
  const canCreate = hasModuleAccess(user, moduleKey, "EDIT");

  const clearStatusTimer = () => {
    if (statusTimer.current) {
      clearTimeout(statusTimer.current);
      statusTimer.current = null;
    }
  };

  const setSuccessStatus = () => {
    clearStatusTimer();
    setUploadStatus("success");
    statusTimer.current = setTimeout(() => setUploadStatus("idle"), 2500);
  };

  const uploadFile = async (file) => {
    const ext = `.${file.name.split(".").pop()?.toLowerCase() || ""}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError("File type not allowed.");
      setUploadStatus("error");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setError("File too large.");
      setUploadStatus("error");
      return;
    }

    setError("");
    setLoading(true);
    setUploadProgress(0);
    setUploadStatus("uploading");
    try {
      const uploadFn = isRisk ? uploadRiskEvidenceFile : uploadControlEvidenceFile;
      const { data } = await uploadFn(parentId, file, {
        onUploadProgress: (evt) => {
          if (!evt?.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(pct);
        },
      });
      onCreated?.(data);
      setSuccessStatus();
    } catch (err) {
      const status = err?.response?.status;
      setError(mapError(status));
      setUploadStatus("error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    event.target.value = "";
  };

  const handleUrlSubmit = async (event) => {
    event.preventDefault();
    if (!url) return;
    setError("");
    setLoading(true);
    setUploadStatus("uploading");
    try {
      const uploadFn = isRisk ? uploadRiskEvidenceUrl : uploadControlEvidenceUrl;
      const { data } = await uploadFn(parentId, url);
      onCreated?.(data);
      setUrl("");
      setSuccessStatus();
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 422 ? "Invalid URL." : mapError(status));
      setUploadStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!loading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (loading) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  if (!canCreate) {
    return (
      <div
        className="evidence-uploader"
        style={{
          border: "1px dashed #cbd5e1",
          borderRadius: 10,
          padding: 12,
          color: "#64748b",
          background: "rgba(248, 250, 252, 0.92)",
        }}
      >
        Uploading evidence requires Edit access to this module.
      </div>
    );
  }

  return (
    <div className="evidence-uploader">
      <div className="evidence-uploader__mode">
        <Button
          onClick={() => setMode("file")}
          disabled={mode === "file"}
          variant={mode === "file" ? "primary" : "secondary"}
          style={{ padding: "6px 12px" }}
        >
          File
        </Button>
        <Button
          onClick={() => setMode("url")}
          disabled={mode === "url"}
          variant={mode === "url" ? "primary" : "secondary"}
          style={{ padding: "6px 12px" }}
        >
          URL
        </Button>
      </div>

      {mode === "file" ? (
        <div
          className="evidence-uploader__file"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `1px dashed ${isDragging ? "#0f766e" : "#cbd5f5"}`,
            padding: "12px",
            borderRadius: 8,
            background: isDragging ? "rgba(15, 118, 110, 0.06)" : "transparent",
          }}
        >
          <input type="file" onChange={handleFileChange} disabled={loading} />
          <p className="hint" style={{ marginTop: 8 }}>
            Drag and drop a file here, or click to choose. PDF/PNG/JPEG up to 20MB.
          </p>
        </div>
      ) : (
        <form onSubmit={handleUrlSubmit} className="evidence-uploader__url">
          <input
            type="url"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={loading}
          />
          <Button type="submit" disabled={loading || !url} variant="primary" style={{ padding: "6px 12px" }}>
            Add URL
          </Button>
        </form>
      )}

      {loading && (
        <div className="status">
          Uploading{uploadProgress ? ` ${uploadProgress}%` : "..."}
        </div>
      )}
      {uploadStatus === "success" && !loading && (
        <div className="status" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#16a34a",
            }}
          />
          Uploaded
        </div>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

export default EvidenceUploader;
